import React, { useEffect, useState } from 'react'

import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { getExpenseDetails } from 'api/requests'
import { ExpenseDetails } from 'models/ExpenseDetails'
import moment from 'moment'

import IconContainer from 'components/IconContainer/IconContainer'

import { formatMoney } from 'utils/FormatMoney'

type Props = {
  isOpen: boolean
  handleClose: () => void
  uuid: string
  hideTransferRow?: boolean
  hideDebitedDate?: boolean
  onOpenBillingDetail?: (billingUuid: string) => void
  onOpenNegativationDetail?: (uuid: string) => void
  onOpenNfseDetail?: (uuid: string) => void
  onOpenTransferDetail?: (uuid: string) => void
}

const EXPENSE_TYPE_LABELS: Record<string, string> = {
  nfse: 'Cobrança de NFSe',
  contract: 'Cobrança de Contrato',
  extra_notification: 'Cobrança de Notificação',
  billing_no_payment_report: 'Cobrança de Negativação',
  refund: 'Estorno',
  chargeback: 'Chargeback',
  transfer: 'Custo de Transferência',
  saas: 'Mensalidade',
  fee_payment: 'Taxa'
}

const EXPENSEABLE_BUTTON_LABELS: Partial<Record<string, string>> = {
  Nfse: 'Ver nota fiscal',
  Contract: 'Ver contrato',
  BillingNoPaymentReport: 'Ver negativação',
  Billing: 'Ver cobrança',
  SaasBilling: 'Ver fatura',
  Transfer: 'Ver transferência'
}

const NOTIFICATION_EXPENSE_TYPE = 'extra_notification'

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  debited: 'Debitado',
  canceled: 'Cancelado',
  avaiable: 'Disponível',
  blocked: 'Bloqueado'
}

const formatDate = (date: string | null) => {
  if (!date) return '-'
  return moment(date.slice(0, 10), 'YYYY-MM-DD').format('DD/MM/YYYY')
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" gap={1.25} alignItems="end" margin="10px 0px 10px 0px">
    <Typography variant="smLight" color="neutral.500">
      {label}
    </Typography>
    <Box
      sx={{
        flexGrow: 1,
        borderTop: '0.5px dashed #CBCBCB',
        alignSelf: 'center',
        marginTop: '2px'
      }}
    />
    <Typography variant="smBold">{value}</Typography>
  </Box>
)

const DESCRIPTION_INLINE_THRESHOLD = 45

const DescriptionRow = ({ value }: { value: string }) => {
  if (value.length <= DESCRIPTION_INLINE_THRESHOLD) {
    return <Row label="Descrição" value={value} />
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={0.5}
      margin="10px 0px 10px 0px"
    >
      <Typography variant="smLight" color="neutral.500">
        Descrição
      </Typography>
      <Typography variant="smBold" sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
}

const ModalBalanceExpenseDetail = ({
  isOpen,
  handleClose,
  uuid,
  hideTransferRow = false,
  hideDebitedDate = false,
  onOpenBillingDetail,
  onOpenNegativationDetail,
  onOpenNfseDetail,
  onOpenTransferDetail
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ExpenseDetails | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, handleClose])

  useEffect(() => {
    if (!isOpen || !uuid) return

    const fetch = async () => {
      setLoading(true)
      setData(null)
      try {
        const res = await getExpenseDetails({ uuid })
        setData((res as any)?.data?.expense ?? null)
      } finally {
        setLoading(false)
      }
    }

    fetch()
  }, [isOpen, uuid])

  const handleNavigateToExpenseable = () => {
    if (!data?.expenseable) return

    // Mensalidade: a fatura é da conta da Base, não do merchant. Abrir como
    // cobrança dele dá 404; o link público mostra a composição (SC-11553).
    if (data.expenseable.type === 'SaasBilling' && data.expenseable.url) {
      window.open(data.expenseable.url, '_blank')
      return
    }

    if (
      data.expenseable.type === 'Billing' &&
      onOpenBillingDetail &&
      data.expenseable.uuid
    ) {
      onOpenBillingDetail(data.expenseable.uuid)
      return
    }

    if (
      data.expenseable.type === 'BillingNoPaymentReport' &&
      onOpenNegativationDetail &&
      data.expenseable.uuid
    ) {
      onOpenNegativationDetail(data.expenseable.uuid)
      return
    }

    if (
      data.expenseable.type === 'Nfse' &&
      onOpenNfseDetail &&
      data.expenseable.uuid
    ) {
      onOpenNfseDetail(data.expenseable.uuid)
      return
    }

    if (
      data.expenseable.type === 'Transfer' &&
      onOpenTransferDetail &&
      data.expenseable.uuid
    ) {
      onOpenTransferDetail(data.expenseable.uuid)
      return
    }

    const expenseableUuid = data.expenseable.uuid
    const routes: Partial<Record<string, string>> = {
      Contract: expenseableUuid ? `/contratos/${expenseableUuid}` : '/contratos'
    }

    const route = routes[data.expenseable.type]
    if (route) {
      window.open(route, '_blank')
    }
  }

  const handleOpenCustomerHistory = () => {
    if (!data?.related_customer?.uuid) return
    window.open(
      `/clientes/${data.related_customer.uuid}?tab=relacionamento`,
      '_blank'
    )
  }

  const showExpenseableButton =
    data?.expenseable && EXPENSEABLE_BUTTON_LABELS[data.expenseable.type]

  const showNotificationButton =
    data?.expense_type === NOTIFICATION_EXPENSE_TYPE &&
    !!data?.related_customer?.uuid

  const title = data
    ? EXPENSE_TYPE_LABELS[data.expense_type] ?? 'Despesa'
    : 'Detalhe da Despesa'

  return (
    <Modal
      handleClose={handleClose}
      open={isOpen}
      width="100%"
      maxWidth="520px"
      maxHeight="90vh"
    >
      <ModalHeader onClose={handleClose}>
        <Stack direction="row" alignItems="center" gap={2}>
          <IconContainer icon="paid" />
          <Typography variant="mdBold">{title}</Typography>
        </Stack>
      </ModalHeader>

      <ModalContent>
        {loading && (
          <Stack gap={1} px={0.5}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={28} />
            ))}
          </Stack>
        )}

        {!loading && data && (
          <Box px={0.5}>
            <Row label="Valor" value={formatMoney(Number(data.amount))} />
            <Row
              label="Status"
              value={STATUS_LABELS[data.status] ?? data.status}
            />
            <Row label="Data prevista" value={formatDate(data.expected_on)} />
            {!hideDebitedDate && (
              <Row
                label="Data debitada"
                value={formatDate(data.date_debited)}
              />
            )}

            {data.transfer && !hideTransferRow && (
              <Row
                label="Transferência"
                value={`${formatMoney(
                  Number(data.transfer.amount)
                )} — ${formatDate(data.transfer.created_at)}`}
              />
            )}

            <DescriptionRow
              value={
                data.expense_type === 'contract' && data.description
                  ? `${data.description.split(' | ')[0]} — ${
                      data.description.split(' | ')[1] ?? ''
                    }`
                  : data.description ?? '-'
              }
            />
          </Box>
        )}

        {!loading && !data && (
          <Typography variant="smLight" color="neutral.500" px={0.5}>
            Não foi possível carregar os detalhes.
          </Typography>
        )}
      </ModalContent>

      {(showExpenseableButton || showNotificationButton) && (
        <ModalFooter>
          {showExpenseableButton && data?.expenseable && (
            <Button onClick={handleNavigateToExpenseable}>
              {EXPENSEABLE_BUTTON_LABELS[data.expenseable.type]}
            </Button>
          )}
          {showNotificationButton && (
            <Button onClick={handleOpenCustomerHistory}>
              Ver histórico de notificações
            </Button>
          )}
        </ModalFooter>
      )}
    </Modal>
  )
}

export default ModalBalanceExpenseDetail
